import { useSession, useUser } from "@clerk/clerk-react";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useSearchParams } from "react-router";
import useWebSocket, { ReadyState } from "react-use-websocket";
import { toast } from "sonner";
import { useDatabaseSyncWrapperStore } from "@/components/contexts/databaseSyncStore";
import {
  fetchDevServerStatus,
  setBitbucketAccess,
  setGithubAccess,
  setGitlabAccess,
} from "@/store/codebaseSlice";
import { fetchGitStatus } from "@/store/gitStatusSlice";
import { setDevServerUrl, setIFrameChanged } from "@/store/iframeSlice";
import { useTypedSelector } from "@/store/store";
import { useTelemetryStore } from "@/store/telemetryStore";
import useActivityStore from "@/store/zustand/activityStore";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import useLayoutStore, { LayoutMode } from "@/store/zustand/layoutStore";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import { useTailwindConfigStore } from "@/store/zustand/tailwindConfigStore";
import { isKubernetesMode } from "@/utils/deploymentMode";
import {
  useDevServerLaunched,
  useLaunchContainer,
  useLoadCodebase,
  useProjectLaunchStatus,
  useStartServer,
} from "@/utils/devServerUtils";
import { track } from "@/utils/events/tracker";
import { GitProvider } from "@/utils/gitProviderUtils";
import { ProjectRunMode } from "@/utils/templateProjects";
import {
  getAxiosErorrMessage,
  getDevServerUrl,
  getUnexpiredToken,
  isDemoUser,
  logAxiosError,
  useActiveMainCanvas,
  useActiveProject,
  useCanvasId,
  usePrevious,
  useUserId,
} from "@/utils/utils";
import { useInstancesStore } from "@/components/AssetsPanel/instances/instancesStore";
import { usePagesStore } from "@/store/zustand/pagesStore";
import { useInitMcp } from "./useInitMcp";
import useLocalMode from "./useLocalMode";
import { useSetupProject } from "./useSetupProject";
import {
  updateFromPersistedPages,
  updateFromIncrementalPageChanges,
} from "@/store/zustand/pagesStore";

export const useProjectLaunch = () => {
  const { user } = useUser();
  const triggerTailwindConfigRefresh = useTailwindConfigStore(
    (state) => state.triggerTailwindConfigRefresh,
  );
  const demoUser = isDemoUser(user);

  const { session } = useSession();

  const location = useLocation();

  const canvasId = useCanvasId();

  const { isLocalMode, localPort } = useLocalMode();

  const initMcp = useInitMcp();

  const dispatch = useDispatch<any>();

  const mainCanvas = useActiveMainCanvas();

  // Codebase store
  const projectLaunchStatus = useProjectLaunchStatus();
  const conditions = useCodebaseStore((state) => state.conditions);

  const updateLookupsBasedOnPartialResponse = useCodebaseStore(
    (state) => state.updateLookupsBasedOnPartialResponse,
  );

  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);

  const triggerRefetchActivityStream = useActivityStore(
    (state) => state.triggerRefetchActivityStream,
  );

  const devServerStatus = useTypedSelector(
    (state) => state.codebase.devServerStatus,
  );
  const devServerRequestStatus = useSelector(
    (state: any) => state.codebase.devServerRequestStatus,
  );
  const devServerRequestStatusRef = useRef(devServerRequestStatus);
  useEffect(() => {
    devServerRequestStatusRef.current = devServerRequestStatus;
  }, [devServerRequestStatus]);

  const startServer = useStartServer();
  const launchContainer = useLaunchContainer();

  // Project from DB
  const project = useActiveProject();

  const projectLoadingProgress = useSelector(
    (state: any) => state.supabaseStore.projectLoadingProgress,
  );

  // For Local Mode - track if the dev server has started up and if it disconnects
  const [localModeDevServerStatus, setLocalModeDevServerStatus] =
    useState("loading");

  const prevCanvasId = usePrevious(canvasId);
  const canvasIdRef = useRef(canvasId);
  useEffect(() => {
    canvasIdRef.current = canvasId;
  }, [canvasId]);

  const rehydrateCanvasData = useInstancesStore(
    (state) => state.rehydrateCanvasData,
  );
  const pages = usePagesStore((state) => state.pages[canvasId ?? ""] ?? []);

  // Track which canvas we've already rehydrated to avoid re-running
  const rehydratedCanvasRef = useRef<string | null>(null);

  // Rehydrate persisted instance data from Supabase for the current canvas
  // Only runs once per canvas, after pages are loaded
  useEffect(() => {
    // Only rehydrate if:
    // 1. Supabase client is initialized
    // 2. We have a canvasId
    // 3. Pages are loaded (not empty - means we have actual storyboard data)
    // 4. We haven't already rehydrated this canvas
    if (
      supabaseClient &&
      canvasId &&
      pages.length > 0 &&
      rehydratedCanvasRef.current !== canvasId
    ) {
      rehydratedCanvasRef.current = canvasId;

      // Collect all valid storyboard IDs from pages
      const validStoryboardIds = new Set<string>();
      for (const page of pages) {
        for (const sb of page.storyboards ?? []) {
          validStoryboardIds.add(sb.id);
        }
      }

      rehydrateCanvasData(canvasId, validStoryboardIds);
    }
  }, [supabaseClient, canvasId, pages.length > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  const runningProjectLoadRef = useRef(false);
  const everRanProjectLoadRef = useRef(false);

  const loadCodebase = useLoadCodebase();

  const projectRef = useRef(project);
  useEffect(() => {
    projectRef.current = project;
  }, [project]);

  const unauthorized = useSelector((state: any) => state.codebase.unauthorized);
  const deleted = useSelector((state: any) => state.codebase.deleted);
  const basePathToProject = useSelector(
    (state: any) => state.codebase.basePathToProject,
  );

  const [triggerFetchDevServerStatus, setTriggerFetchDevServerStatus] =
    useState(false);

  /**
   * When the project launch status changes, trigger a fetch of the dev server status
   */
  useEffect(() => {
    if (devServerRequestStatusRef.current === "loading") {
      return;
    }

    setTriggerFetchDevServerStatus(true);
  }, [projectLaunchStatus]);

  /**
   * Fetch the dev server status which is needed to know how to launch a project
   */
  useEffect(() => {
    if (!session || isLocalMode || !canvasId) {
      return;
    }

    setTriggerFetchDevServerStatus(false);

    dispatch(
      fetchDevServerStatus({
        canvasId,
        supabaseClient,
        session,
      }),
    );

    // Fetch once every 5 seconds, and once every minute for expo (because it takes longer)
    // Also, only do this if the project launch status is succeeded
    const intervalId = setInterval(() => {
      setTriggerFetchDevServerStatus(true);
    }, 5000);

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [
    canvasId,
    Boolean(session),
    isLocalMode,
    triggerFetchDevServerStatus,
    projectLaunchStatus,
  ]);

  const [fetchedGitStatus, setFetchedGitStatus] = useState(false);

  const userId = useUserId();

  /**
   * Check if user has access to Github project.
   */
  useEffect(() => {
    // Already fetched
    if (fetchedGitStatus) {
      return;
    }

    if (!session || !canvasId || unauthorized === true || deleted) {
      return;
    }

    if (!project?.repoPath) {
      return;
    }

    // Data not loaded yet
    if (!projectLoadingProgress || !mainCanvas || !user) {
      return;
    }

    // Only check Github access if the user owns the canvas, or if the project needs to be cloned
    if (
      mainCanvas.owner_user_id !== userId &&
      projectLoadingProgress.cloning_done
    ) {
      return;
    }

    setFetchedGitStatus(true);

    const fetchGitlabAccess = async () => {
      // automatically return true if the project is not a gitlab project - we shouldn't check for gitlab access
      if (project.provider !== "gitlab") {
        dispatch(setGitlabAccess(true));
        return;
      }

      try {
        const supabaseJWT = await getUnexpiredToken(session);

        const response = await axios.post(
          `${import.meta.env.VITE_BACKEND_BASE_URL}/gitProvider/repos`,
          {
            repoPath: project.repoPath,
            gitProvider: project.provider,
            gitlab_project_id: project.gitlab_project_id,
          },
          {
            headers: {
              Authorization: `Bearer ${supabaseJWT}`,
            },
          },
        );

        if (response.status === 200) {
          dispatch(setGitlabAccess(true));
        } else {
          dispatch(setGitlabAccess(false));
        }
      } catch (error) {
        console.error("Error fetching gitlab access", error);
        track("PROJECT_LAUNCH_ERROR", {
          error: "Failed to fetch gitlab access",
          data: error,
        });

        if (error.response && error.response.status === 404) {
          dispatch(setGitlabAccess(false));
        }
      }
    };

    const fetchBitbucketAccess = async () => {
      // automatically return true if the project is not a bitbucket project - we shouldn't check for bitbucket access
      if (project.provider !== "bitbucket") {
        dispatch(setBitbucketAccess(true));
        return;
      }

      try {
        const supabaseJWT = await getUnexpiredToken(session);

        const response = await axios.post(
          `${import.meta.env.VITE_BACKEND_BASE_URL}/gitProvider/repos`,
          {
            repoPath: project.repoPath,
            gitProvider: project.provider,
          },
          {
            headers: {
              Authorization: `Bearer ${supabaseJWT}`,
            },
          },
        );

        if (response.status === 200) {
          dispatch(setBitbucketAccess(true));
        } else {
          dispatch(setBitbucketAccess(false));
        }
      } catch (error) {
        console.error("Error fetching bitbucket access", error);
        track("PROJECT_LAUNCH_ERROR", {
          error: "Failed to fetch bitbucket access",
          data: error,
        });

        if (error.response && error.response.status === 404) {
          dispatch(setBitbucketAccess(false));
        }
      }
    };

    const fetchGithubAccess = async () => {
      const provider = project.provider;

      // automatically return true if the project is not a github project - we shouldn't check for gitlab access
      // we also default to github if there is no provider set so check that there also is a provider
      if (provider && provider !== GitProvider.GITHUB) {
        dispatch(setGithubAccess(true));
        return;
      }

      try {
        const supabaseJWT = await getUnexpiredToken(session);

        const response = await axios.post(
          `${import.meta.env.VITE_BACKEND_BASE_URL}/gitProvider/repos`,
          {
            repoPath: project.repoPath,
            gitProvider: provider,
          },
          {
            headers: {
              Authorization: `Bearer ${supabaseJWT}`,
            },
          },
        );

        if (response.status === 200) {
          dispatch(setGithubAccess(true));
        } else {
          dispatch(setGithubAccess(false));
        }
      } catch (error) {
        if (error.response && error.response.status === 404) {
          dispatch(setGithubAccess(false));
        }
      }
    };

    fetchGithubAccess();
    fetchGitlabAccess();
    fetchBitbucketAccess();
  }, [
    Boolean(session),
    unauthorized,
    deleted,
    canvasId,
    project,
    user,
    projectLoadingProgress,
  ]);

  /**
   * File watcher
   */
  const [socketUrl, setSocketUrl] = useState(null);
  const { sendMessage, lastMessage, readyState } = useWebSocket(socketUrl, {
    reconnectAttempts: 0,
    heartbeat: {
      timeout: 30000,
    },
  });

  const [fetchingSocketUrl, setFetchingSocketUrl] = useState(false);

  useEffect(() => {
    // Reset so it tries to open again
    if (
      readyState === ReadyState.CLOSED ||
      readyState === ReadyState.UNINSTANTIATED
    ) {
      setSocketUrl(null);
    }
  }, [readyState]);

  /**
   * Load the websocket token & url for the file watcher, but only after the project has been cloned
   */
  useEffect(() => {
    if (!session || !canvasId || !projectLoadingProgress?.files_loaded) {
      return;
    }

    if (fetchingSocketUrl || socketUrl) {
      return;
    }

    if (
      !(
        readyState === ReadyState.CLOSED ||
        readyState === ReadyState.UNINSTANTIATED
      )
    ) {
      return;
    }

    setFetchingSocketUrl(true);

    getUnexpiredToken(session).then((supabaseJWT) =>
      axios
        .get(
          `${
            import.meta.env.VITE_BACKEND_BASE_URL
          }/canvases/${canvasId}/websocketToken`,
          {
            headers: {
              Authorization: `Bearer ${supabaseJWT}`,
            },
          },
        )
        .then((response) => {
          const fileWatcherToken = response?.data?.token;
          setSocketUrl(
            // @ts-expect-error TS2345
            `${
              import.meta.env.VITE_BACKEND_WS_BASE_URL
            }/canvases/${canvasId}/fileWatcher?token=${fileWatcherToken}`,
          );
          setFetchingSocketUrl(false);
        })
        .catch((error) => {
          logAxiosError("Error fetching websocket token", error);
          setFetchingSocketUrl(false);
        }),
    );
  }, [
    canvasId,
    Boolean(session),
    readyState,
    projectLoadingProgress?.files_loaded,
    socketUrl,
  ]);

  /**
   * Listen to messages on the file watcher websocket
   */
  useEffect(() => {
    if (!session) {
      return;
    }

    if (lastMessage !== null) {
      if (!lastMessage.data) {
        return;
      }

      const data = JSON.parse(lastMessage.data);
      if (!data) {
        return;
      }

      updateLookupsBasedOnPartialResponse(data);
      triggerRefetchActivityStream();
      dispatch(
        fetchGitStatus({
          canvasId,
          session,
        }),
      );

      const changedFilesList = data.changedFiles;
      if (changedFilesList && Array.isArray(changedFilesList)) {
        // Check if any Tailwind config files were changed and trigger refresh
        const tailwindConfigPatterns = [
          "tailwind.config.js",
          "tailwind.config.ts",
          "tailwind.config.mjs",
          "tailwind.config.cjs",
        ];

        const hasTailwindConfigChanges = changedFilesList.some((path: string) =>
          tailwindConfigPatterns.some(
            (pattern) => path.endsWith(pattern) || path.includes(`/${pattern}`),
          ),
        );

        if (hasTailwindConfigChanges) {
          triggerTailwindConfigRefresh();
        }

        // Update pages from loadAllFiles response (if pages data is included)
        if (data.pages && basePathToProject) {
          // Full sync - replace all pages
          updateFromPersistedPages(
            canvasId,
            data.pages,
            basePathToProject,
            data.tempobookPagesDir,
          );
        } else if (
          (data.changedPages || data.deletedPagePaths) &&
          basePathToProject
        ) {
          // Incremental update - merge changes
          updateFromIncrementalPageChanges(
            canvasId,
            data.changedPages || [],
            data.deletedPagePaths || [],
            basePathToProject,
          );
        }
      }

      dispatch(setIFrameChanged());
    }
  }, [lastMessage, Boolean(session), basePathToProject]);

  const refetchedCanvas = useRef(false);

  const devServerLaunched = useDevServerLaunched();

  useEffect(() => {
    if (devServerLaunched) {
      const devServerUrl = getDevServerUrl(mainCanvas);
      if (devServerUrl) {
        dispatch(
          setDevServerUrl({
            canvasId,
            url: devServerUrl,
          }),
        );
      } else if (
        import.meta.env.VITE_ENVIRONMENT === "DEV" &&
        !refetchedCanvas.current
      ) {
        // Refetch the canvas to get the assigned port
        useDatabaseSyncWrapperStore.getState().triggerRefetchCanvas();
        refetchedCanvas.current = true;
      }
    } else {
      refetchedCanvas.current = false;
    }
  }, [devServerLaunched, mainCanvas?.id, mainCanvas?.assigned_port]);

  /**
   * Check if local mode is connected or not
   */
  useEffect(() => {
    if (!isLocalMode) {
      return;
    }

    let timeoutId;
    let useEffectCleared = false;

    const checkIfDevServerRunning = () => {
      axios
        .get("http://localhost:2993")
        .then(() => {
          setLocalModeDevServerStatus("running");

          if (!useEffectCleared) {
            timeoutId = setTimeout(checkIfDevServerRunning, 3000);
          }
        })
        .catch((error) => {
          if (localModeDevServerStatus === "running") {
            setLocalModeDevServerStatus("disconnected");
          }

          if (!useEffectCleared) {
            timeoutId = setTimeout(checkIfDevServerRunning, 3000);
          }
        });
    };

    checkIfDevServerRunning();

    return () => {
      useEffectCleared = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isLocalMode, localModeDevServerStatus]);

  const loadedDevServerStatus = Boolean(devServerStatus);

  /**
   * When the user is logged in, load and launch the project
   */
  useEffect(() => {
    if (!session || !supabaseClient) {
      return;
    }

    if (runningProjectLoadRef.current) {
      return;
    }

    if (!project) {
      return;
    }

    if (!loadedDevServerStatus && !isLocalMode && !isKubernetesMode()) {
      return;
    }

    if (!project || !mainCanvas) {
      return;
    }

    if (everRanProjectLoadRef.current && canvasId === prevCanvasId) {
      return;
    }

    runningProjectLoadRef.current = true;
    useCodebaseStore.getState().setProjectLaunchRunning(true);

    everRanProjectLoadRef.current = true;
    useCodebaseStore.getState().setProjectLaunchEverRan(true);

    useCodebaseStore.getState().setProjectLaunchHadError(false);

    // Container launch tests
    const needToLaunchContainer =
      (!devServerStatus?.containerStatus?.running ||
        !devServerStatus?.containerStatus?.configUpToDate) &&
      !isLocalMode &&
      project?.runMode === ProjectRunMode.CLOUD;

    // Dev server launch tests
    const needToStartDevServer =
      needToLaunchContainer ||
      ((!devServerStatus?.isServerRunning ||
        !devServerStatus?.devServerConsoleConnected) &&
        !isLocalMode &&
        project?.runMode === ProjectRunMode.CLOUD);

    const run = async () => {
      const updateDict = {
        canvas_id: canvasId,
        files_loaded: false,
        devtools_script_responsive: false,
        last_updated: new Date().toISOString(),
        initialized_env: true,
      };

      if (needToLaunchContainer) {
        updateDict["launched_container"] = false;
      } else {
        updateDict["launched_container"] = true;
        updateDict["cloning_done"] = true;
        updateDict["stopped_container"] = true;
      }

      if (needToStartDevServer) {
        updateDict["started_dev_server"] = false;
        updateDict["node_modules_installed"] = false;
        updateDict["dev_server_responding"] = false;
      } else {
        updateDict["started_dev_server"] = true;
        updateDict["node_modules_installed"] = true;
        updateDict["dev_server_responding"] = true;
        updateDict["cloning_done"] = true;
      }

      const { error } = await supabaseClient
        .from("project_loading_progress")
        .upsert(updateDict, {
          onConflict: "canvas_id",
        });

      useDatabaseSyncWrapperStore
        .getState()
        .triggerRefetchProjectLoadingProgress();

      if (error) {
        console.error("Update progress error", error);
      }
      // TODO: make this better by first checking if the project has already been cloned & lookups loaded

      try {
        // Generate a unique trace ID for this project launch flow
        const projectLaunchTraceId = crypto.randomUUID();

        // Start telemetry tracking for nav tree loading, await it so that
        // all successive events have the correct startEvent in their traces
        await useTelemetryStore.getState().startProjectLaunch({
          canvasId,
          traceId: projectLaunchTraceId,
        });

        // Always launch the container no matter what
        if (needToLaunchContainer) {
          const { success, error } = await launchContainer({
            traceId: projectLaunchTraceId,
          });

          if (!success) {
            track("PROJECT_LAUNCH_ERROR", {
              error: "Failed to launch container",
              data: error,
            });
            useCodebaseStore.getState().setProjectLaunchHadError(true);
            return;
          }
        }

        const loadResponse = await loadCodebase({
          traceId: projectLaunchTraceId,
        });

        if (!loadResponse) {
          useCodebaseStore.getState().setProjectLaunchHadError(true);
          track("PROJECT_LAUNCH_ERROR", {
            error: "Failed to load codebase",
            data: loadResponse,
          });
          return;
        }

        if (needToStartDevServer) {
          const { success } = await startServer({
            doNotCheckContainer: true,
            doNotReloadCodebase: true,
            traceId: projectLaunchTraceId,
          });
          if (!success) {
            track("PROJECT_LAUNCH_ERROR", {
              error: "Failed to start dev server",
              data: error,
            });
            useCodebaseStore.getState().setProjectLaunchHadError(true);
            useLayoutStore.getState().setNewLayout(undefined, LayoutMode.Code);
            return;
          }
        }

        // Mark project launch request complete so telemetry only accepts nav trees built after this point
        useTelemetryStore
          .getState()
          .markProjectLaunchRequestComplete({ canvasId });

        useDatabaseSyncWrapperStore
          .getState()
          .triggerRefetchProjectLoadingProgress();
        if (canvasId !== canvasIdRef.current) {
          return;
        }

        setTriggerFetchDevServerStatus(true);
        track("PROJECT_LAUNCHED");

        // Initialize MCP for Claude Code CLI
        initMcp();
      } catch (error) {
        if (canvasId !== canvasIdRef.current) {
          return;
        }

        logAxiosError("Error launching project", error);
        toast.error("Error launching project: " + getAxiosErorrMessage(error));
        return;
      }
    };

    run().then(() => {
      runningProjectLoadRef.current = false;
      useCodebaseStore.getState().setProjectLaunchRunning(false);
    });
  }, [
    projectLaunchStatus,
    project,

    // Only trigger use effect when the dev server status is created, not when changed
    loadedDevServerStatus,
    Boolean(session),
    Boolean(supabaseClient),
  ]);

  const [searchParams, setSearchParams] = useSearchParams();
  const initialSetupRepo = searchParams.get("setup-repo") === "t";
  const guessFramework = searchParams.get("guess-framework") === "t";
  const guessPackageManager = searchParams.get("guess-package-manager") === "t";
  const guessLanguage = searchParams.get("guess-language") === "t";
  const shouldSetupRepo = initialSetupRepo;
  const conditionsLoaded = Object.keys(conditions || {}).length > 0;
  const [settingUpRepo, setSettingUpRepo] = useState(false);

  const { setupProject } = useSetupProject();

  useEffect(() => {
    if (!supabaseClient) {
      return;
    }

    if (!projectLoadingProgress?.cloning_done) {
      return;
    }

    if (!conditionsLoaded) {
      return;
    }

    if (shouldSetupRepo && !settingUpRepo) {
      setSettingUpRepo(true);
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete("setup-repo");
        return newParams;
      });
      setupProject(canvasId, {
        guessFramework,
        guessPackageManager,
        guessLanguage,
      });
    } else if (!shouldSetupRepo && settingUpRepo) {
      setSettingUpRepo(false);
      const markInstalled = async () => {
        await supabaseClient
          .from("project_loading_progress")
          .update({
            installed_devtools: true,
            last_updated: new Date().toISOString(),
          })
          .eq("canvas_id", canvasId);

        useDatabaseSyncWrapperStore
          .getState()
          .triggerRefetchProjectLoadingProgress();
      };
      markInstalled();
    }
  }, [
    shouldSetupRepo,
    initialSetupRepo,
    settingUpRepo,
    supabaseClient,
    setSearchParams,
    canvasId,
    projectLoadingProgress?.cloning_done,
    setupProject,
    guessFramework,
    guessPackageManager,
    guessLanguage,
    conditionsLoaded,
  ]);
};
